import * as React from "react";
import { Suspense, lazy, Fragment } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
// Use react-helmet-async for improved TypeScript support
import { HelmetProvider, Helmet } from "react-helmet-async";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

// Lazy load components with dynamic imports
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const DashboardLayout = lazy(() => import("./components/layouts/DashboardLayout"));
const Dashboard = lazy(() => import("./pages/Dashboard"));

// Monitoring pages
const HackathonList = lazy(() => import("./pages/monitoring/HackathonList"));
const HackathonDetails = lazy(() => import("./pages/monitoring/HackathonDetails"));
const HackathonSubmissions = lazy(() => import("./pages/monitoring/HackathonSubmissions"));
const HackathonScoreReport = lazy(() => import("./pages/monitoring/HackathonScoreReport"));
const ProjectDetails = lazy(() => import("./pages/monitoring/ProjectDetails"));

export default function App() {
  // Define title and metadata separately
  const pageTitle = "HackScore Monitoring Dashboard";
  const pageDescription = "Staff monitoring dashboard for hackathon data";
  
  return (
    <BrowserRouter>
      <HelmetProvider>
        {/* Use Helmet to set document head */}
        <Helmet>
          {/* @ts-ignore - Known TypeScript issue with Helmet elements */}
          <title>{pageTitle}</title>
          {/* @ts-ignore - Known TypeScript issue with Helmet elements */}
          <meta name="description" content={pageDescription} />
        </Helmet>
        
        <QueryClientProvider client={queryClient}>
          <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
            <Suspense
              fallback={
                /* @ts-ignore - Known TypeScript issue with JSX elements */
                <div className="flex items-center justify-center min-h-screen">
                  <LoadingSpinner />
                </div>
              }
            >
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/dashboard" element={<DashboardLayout />}>
                  <Route index element={<Dashboard />} />
                  <Route path="hackathons" element={<HackathonList />} />

                  {/* Hackathon specific routes - read-only monitoring */}
                  <Route path="hackathon/:hackathonId" element={<HackathonDetails />} />
                  <Route path="hackathon/:hackathonId/submissions" element={<HackathonSubmissions />} />
                  <Route path="hackathon/:hackathonId/score-report" element={<HackathonScoreReport />} />
                  <Route path="project/:projectId" element={<ProjectDetails />} />
                </Route>
              </Routes>
            </Suspense>
            <Toaster />
          </ThemeProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </BrowserRouter>
  );
}
